// Central frontend configuration
// Single source of truth for API base URL and default AI provider/models.
// Values are intentionally identical to the previous inline hardcodes —
// this file only centralizes them.

/** Base URL for the backend API proxy (same-origin in production). */
export const API_BASE_URL = '/api';

/** Default AI provider — Gemini (OpenRouter free models are persistently rate-limited). */
export const DEFAULT_PROVIDER: 'openrouter' | 'gemini' = 'gemini';

/**
 * Default Gemini model used when no model is explicitly selected.
 *
 * gemini-3.1-flash-lite (since 2026-09-30, model + prompt evaluation on 4 synthetic
 * profiles, vps-atlas research/wiscv-model-eval.md):
 *  - with the v2 prompt its quality is within noise of 2.5 Flash (judge 4.5 vs 4.2,
 *    0 critical errors for both); the prompt, not the model, drives quality;
 *  - free tier ~500 requests/day per project vs 20 for Flash models (the limit is
 *    shared by all psychologists using the app);
 *  - fastest (~15 s per report) and cheapest (~0.003 USD per report), GA/stable;
 *  - not affected by Google restricting 2.5 models to already-active projects
 *    (2026-09-18), so a new key/project keeps working.
 * History: 2.5-flash was chosen over 3.5-flash because 3.5 with a dynamic thinking
 * budget burned the whole token limit and returned 0 bytes (QA DEV 2026-06-10); the
 * backend now pins the thinking budget, so that failure mode is fixed for all >= 2.5.
 */
export const DEFAULT_GEMINI_MODEL = 'gemini-3.1-flash-lite';

/**
 * Gemini fallback chain tried after the selected model fails with 503 (after one
 * retry), 429 or 500 — see services/geminiFallback.ts. Only ids present in the
 * current catalog are used. Order: 2.5 Flash (best v2 score among fallbacks, 8/8
 * available in the evaluation), then 3.5 Flash-Lite (500 RPD, not affected by the
 * 2.5 access restriction). A 400 (bad request) never falls back.
 */
export const GEMINI_FALLBACK_CHAIN: readonly string[] = ['gemini-2.5-flash', 'gemini-3.5-flash-lite'];

/*
 * No default OpenRouter model on purpose. The previous hardcoded id
 * (meta-llama/llama-3.3-70b-instruct:free) vanished from the OpenRouter catalog
 * (evaluation round 2, 2026-09-30) and free models come and go; Gemini is the main
 * path. The OpenRouter list comes only from the live catalog (GET /api/ai/models/openrouter)
 * and a request without an explicitly chosen model fails with a clear message.
 */

/**
 * Default of the OpenRouter-only "Model zapasowy" selector (a non-empty value enables
 * the OpenRouter 429 → Gemini switch, which itself uses the Gemini chain starting at
 * DEFAULT_GEMINI_MODEL). Was gemini-3-flash-preview — the weakest model in the
 * 2026-09-30 evaluation (most critical errors, truncated 1 of 4 reports).
 */
export const FALLBACK_MODEL = GEMINI_FALLBACK_CHAIN[0];

/** Previous FALLBACK_MODEL value; a saved choice equal to it is migrated on load. */
export const LEGACY_FALLBACK_MODEL = 'gemini-3-flash-preview';

/** localStorage key under which the user's chosen fallback model is persisted. */
export const FALLBACK_MODEL_STORAGE_KEY = 'wiscv_fallback_model';

/** localStorage key with the user's chosen Gemini model (model id only — not PII). */
export const GEMINI_MODEL_STORAGE_KEY = 'wiscv_gemini_model';
